/**
 * The score's data (audio-direction §4): regions and modes (M3 switches the region), the intensity state
 * machine's triggers and hysteresis, harmony (a Markov chain over scale degrees), per-state layer levels,
 * rhythm pattern families, the Charter and Spacer motifs, instrument levels, stingers (M2: fracture success and
 * the ownership event), the station radio (M2), and the offline-rendered BRAAAM and Shepard riser recipes.
 * `music.json` validated with Zod.
 */
import { z } from 'zod'
import { recipeSchema } from '../sfxSchema'
import musicJson from './music.json'

export const LAYER_NAMES = ['pad', 'organ', 'pluck', 'ostA', 'ostB', 'brass', 'sub', 'shepard', 'taiko', 'metal', 'snare', 'drone'] as const
export type LayerName = (typeof LAYER_NAMES)[number]

const range = (min: number, max: number): z.ZodType<[number, number]> =>
  z
    .tuple([z.number().min(min).max(max), z.number().min(min).max(max)])
    .refine((r) => r[0] <= r[1], { message: 'range must be [min, max]' })
const step16 = z.array(z.number().int().min(-1).max(8)).length(16)
const hits16 = z.array(z.number().min(0).max(1)).length(16)
const levels4 = z.tuple([z.number().min(0).max(2), z.number().min(0).max(2), z.number().min(0).max(2), z.number().min(0).max(2)])
const db = z.number().min(-60).max(12)
const pos = z.number().positive()
const ostSchema = z.object({
  db,
  wave: z.enum(['sawtooth', 'square', 'triangle']),
  q: pos,
  /** Filter envelope: from base × 2^octaves down to base over filterDecay. */
  base: pos,
  octaves: z.number().min(0).max(8),
  filterDecay: pos,
  decay: pos,
  sustain: z.number().min(0).max(1),
  release: pos,
  drive: z.number().min(0).max(40),
})
/** [scale degree, octave offset, start 16th, length in 16ths] over two bars. */
const motifNote = z.tuple([z.number().int().min(-7).max(14), z.number().int().min(-3).max(3), z.number().int().min(0).max(31), z.number().int().min(1).max(32)])

export const regionSchema = z.object({
  mode: z.string(),
  /** Candidate tonic pitch classes (0 = C); the seed picks one. */
  roots: z.array(z.number().int().min(0).max(11)).min(1),
  tempo: range(40, 220),
  colour: z.string(),
})

export const musicSchema = z.object({
  seed: z.number().int().nonnegative(),
  region: z.string(),
  regions: z.record(z.string(), regionSchema),
  modes: z.record(z.string(), z.array(z.number().int().min(0).max(11)).min(5).max(8)),
  intensity: z.object({
    /** Raiders inside this distance (m) make it Tension; inside `combatRange`, or trading fire, Combat. */
    tensionRange: z.number().positive(),
    combatRange: z.number().positive(),
    /** Hits taken or shots fired count as engagement for this long. */
    engageSeconds: z.number().positive(),
    /** Hysteresis (audio-direction §4.3): Combat → Tension after 8 s, Tension → Calm after 20 s without triggers. */
    fallCombatSeconds: z.number().positive(),
    fallTensionSeconds: z.number().positive(),
    feedHz: z.number().positive().max(60),
    braaamCooldownSeconds: z.number().nonnegative(),
  }),
  harmony: z.object({
    /** Bars per chord in Calm, Tension, Combat, Climax. */
    chordBars: z.tuple([z.number().int().min(1), z.number().int().min(1), z.number().int().min(1), z.number().int().min(1)]),
    /** Scale degrees (0-based) chords may be built on, and the transition weights between them (row = from). */
    degrees: z.array(z.number().int().min(0).max(7)).min(2),
    weights: z.array(z.array(z.number().min(0))),
    /** In Combat, the chance a bar stays on the tonic pedal instead of moving. */
    combatPedal: z.number().min(0).max(1),
  }),
  layers: z.object({
    names: z.array(z.enum(LAYER_NAMES)),
    /** Level per state (Calm, Tension, Combat, Climax) of each layer. */
    levels: z.object(Object.fromEntries(LAYER_NAMES.map((n) => [n, levels4])) as Record<LayerName, typeof levels4>),
    /** A rise fades layers in over this many beats; a fall fades them over this many bars (graceful exits). */
    riseBeats: z.number().positive(),
    fallBars: z.number().positive(),
    deathFadeSeconds: z.number().positive(),
  }),
  patterns: z.object({
    ostinato: z.array(step16).min(1),
    accents: z.array(hits16).min(1),
    taikoLow: z.array(hits16).min(1),
    taikoMid: z.array(hits16).min(1),
    taikoHigh: z.array(hits16).min(1),
    tensionTaiko: z.array(hits16).min(1),
    metal: z.array(hits16).min(1),
    snare: z.array(hits16).min(1),
    /** Per-bar chance of mutating an ostinato step or a drum hit. */
    mutation: z.number().min(0).max(1),
    fillEvery: z.number().int().min(2),
    ghost: z.number().min(0).max(1),
  }),
  motifs: z.object({
    /** The Charter: brass swell degrees, one per two bars. */
    charterBrass: z.array(z.array(z.number().int().min(-7).max(14)).min(2)).min(1),
    /** The Spacer: sparse pluck phrases. */
    spacer: z.array(z.array(motifNote).min(1)).min(1),
    spacerEveryBars: range(1, 64),
    spacerChance: z.number().min(0).max(1),
  }),
  voices: z.object({
    padOctave: z.number().int(),
    organOctave: z.number().int(),
    pluckOctave: z.number().int(),
    ostOctave: z.number().int(),
    ostBOctave: z.number().int(),
    brassOctave: z.number().int(),
    subOctave: z.number().int(),
    droneOctave: z.number().int(),
  }),
  /** Instrument voices (native Web Audio, one small graph per note) and levels. */
  instruments: z.object({
    master: z.object({ db, reverbWet: z.number().min(0).max(1) }),
    pad: z.object({ db, saws: z.number().int().min(1).max(6), detuneCents: z.number().min(0).max(60), cutoff: pos, q: pos, attack: pos, release: pos }),
    organ: z.object({ db, partials: z.array(z.number().min(0).max(1)).min(1).max(32), attack: pos, release: pos }),
    brass: z.object({
      db,
      saws: z.number().int().min(1).max(6),
      detuneCents: z.number().min(0).max(60),
      attack: pos,
      release: pos,
      /** The swell: the low-pass opens from start to the Tension or Combat peak with the attack, then settles to end. */
      cutoffStart: pos,
      cutoffTension: pos,
      cutoffCombat: pos,
      cutoffEnd: pos,
      q: pos,
      drive: z.number().min(0).max(40),
    }),
    sting: z.object({ db, saws: z.number().int().min(1).max(6), detuneCents: z.number().min(0).max(60), attack: pos, decay: pos, cutoff: pos }),
    pluck: z.object({ db, ratio: pos, index: z.number().min(0).max(20), indexDecay: pos, decay: pos, cutoff: pos, delayBeats: pos, feedback: z.number().min(0).max(0.95), delayWet: z.number().min(0).max(1) }),
    ostA: ostSchema,
    ostB: ostSchema,
    sub: z.object({ db, sawDb: db, cutoff: pos }),
    drone: z.object({ db, fifthDb: db, cutoff: pos }),
    shepard: z.object({ db }),
    drums: z.object({ taikoDb: db, metalDb: db, snareDb: db, stingDb: db }),
    braaam: z.object({ db, send: z.number().min(0).max(2) }),
  }),
  /** Reverb send per layer. */
  sends: z.object(Object.fromEntries(LAYER_NAMES.map((n) => [n, z.number().min(0).max(2)])) as Record<LayerName, z.ZodNumber>),
  hall: z.object({ seconds: z.number().positive(), predelay: z.number().nonnegative(), decay: z.number().positive(), lowpassHz: z.number().positive(), modes: z.array(z.number().positive()) }),
  stingers: z.object({
    killCooldownSeconds: z.number().nonnegative(),
    /** Fracture success (M2): a short rising pluck figure on sixteenths, scale degrees above the current chord. */
    fracture: z.object({
      degrees: z.array(z.number().int().min(-7).max(14)).min(2).max(8),
      /** Octave offset from `voices.pluckOctave`. */
      octave: z.number().int().min(-2).max(3),
      velocity: z.number().min(0).max(1),
      db,
      cooldownSeconds: z.number().nonnegative(),
    }),
    /**
     * The ownership event (M2): full Spacer statements over a warm swell `swellBars` long (wave cleared: 2 bars),
     * the other layers ducked to `duck` of their level; the station radio lifts for its length.
     */
    ownership: z.object({
      swellBars: z.number().int().min(1).max(16),
      statements: z.number().int().min(1).max(4),
      duck: z.number().min(0).max(1),
      velocity: z.number().min(0).max(1),
    }),
  }),
  /**
   * Station radio (M2, audio-direction §4.3): while docked the score crossfades into a lo-fi radio chain (band-pass,
   * light bit-crush, a static bed), the level is Calm, and Spacer and Charter fragments come every bar on a sparser
   * layer set (`levels`).
   */
  radio: z.object({
    crossfadeSeconds: z.number().positive().max(10),
    highpassHz: pos,
    lowpassHz: pos,
    q: pos,
    /** Bit depth of the crush, its blend with the band-passed signal, and a light drive before it. */
    bits: z.number().int().min(2).max(16),
    crushMix: z.number().min(0).max(1),
    drive: z.number().min(0).max(40),
    /** Level of the radio path at full mix. */
    wet: z.number().min(0).max(2),
    static: z.object({ db, seconds: pos.max(10), crackles: z.number().int().min(0).max(400) }),
    levels: z.object(Object.fromEntries(LAYER_NAMES.map((n) => [n, z.number().min(0).max(2)])) as Record<LayerName, z.ZodNumber>),
    /** A Charter brass fragment every this many bars, lasting `charterBars`, an octave above the brass. */
    charterEveryBars: z.number().int().min(1).max(16),
    charterBars: z.number().int().min(1).max(4),
    charterVelocity: z.number().min(0).max(1),
  }),
  /** Offline-rendered at music start: the BRAAAM, the Shepard riser and the percussion hits. */
  renders: z.object({ braaam: recipeSchema, shepard: recipeSchema, taikoLow: recipeSchema, taikoMid: recipeSchema, taikoHigh: recipeSchema, metal: recipeSchema, snare: recipeSchema }),
})

export type MusicConfig = z.infer<typeof musicSchema>

export function parseMusicConfig(raw: unknown): MusicConfig {
  const cfg = musicSchema.parse(raw)
  const n = cfg.harmony.degrees.length
  if (cfg.harmony.weights.length !== n || cfg.harmony.weights.some((row) => row.length !== n)) throw new Error('music.json: harmony.weights must be a square matrix over harmony.degrees')
  for (const [id, r] of Object.entries(cfg.regions)) if (cfg.modes[r.mode] === undefined) throw new Error(`music.json: region ${id} uses unknown mode ${r.mode}`)
  if (cfg.regions[cfg.region] === undefined) throw new Error(`music.json: unknown default region ${cfg.region}`)
  if (cfg.radio.lowpassHz <= cfg.radio.highpassHz) throw new Error('music.json: radio.lowpassHz must be above radio.highpassHz')
  return cfg
}

export const musicConfig: MusicConfig = parseMusicConfig(musicJson)
